import { z } from "zod";

/**
 * Stable inbox codes, translated by the web client. The dashboard is an
 * inbox of things that need someone's action, filtered by permission.
 */
export const ACTION_ITEM_CODES = [
  "my_draft_requests",
  "requests_to_review",
  "offers_to_send",
  "offers_awaiting_response",
  "offers_expiring_soon",
  "accepted_offers_to_invoice",
  "draft_invoices",
  "overdue_invoices",
  "failed_emails",
  "timesheets_to_approve",
  "expense_claims_to_pay",
  "low_stock_catalog_items",
  "unscheduled_active_projects",
  "scheduled_offboarding",
] as const;
export type ActionItemCode = (typeof ACTION_ITEM_CODES)[number];

export const ACTION_ITEM_PRIORITIES = ["low", "normal", "high"] as const;
export type ActionItemPriority = (typeof ACTION_ITEM_PRIORITIES)[number];

export const actionItemSchema = z
  .object({
    code: z.enum(ACTION_ITEM_CODES),
    count: z.number().int().min(0),
    href: z.string().trim().min(1).max(2048).nullable().optional(),
    priority: z.enum(ACTION_ITEM_PRIORITIES).default("normal"),
  })
  .strict();
export type ActionItemDto = z.infer<typeof actionItemSchema>;

export const actionItemsResponseSchema = z
  .object({ items: z.array(actionItemSchema) })
  .strict();
export type ActionItemsResponseDto = z.infer<typeof actionItemsResponseSchema>;

export interface DashboardResponse {
  inbox: ActionItemDto[];
  /** Present only for users with finance access. */
  finance?: {
    currency: string;
    outstanding: string;
    overdue: string;
    overdueCount: number;
    invoicedThisMonth: string;
    paidThisMonth: string;
    openOffersValue: string;
    /** Last six months, oldest first. */
    months: Array<{ month: string; invoiced: string; paid: string }>;
  };
  /** The signed-in user's own work today. */
  myDay: {
    date: string;
    /** Tenant time zone; assignment times are shown in it. */
    timeZone: string;
    assignments: Array<{
      id: string;
      projectId: string;
      projectNumber: string;
      projectTitle: string;
      customerName: string;
      startAt: string;
      endAt: string;
      notes?: string;
    }>;
    clockedIn: boolean;
  };
  counts: {
    openRequests?: number;
    /** Planned, active or on-hold projects visible to the caller. */
    openProjects?: number;
  };
}

export const SEARCH_RESULT_TYPES = [
  "customer",
  "request",
  "offer",
  "project",
  "invoice",
  "employee",
  "catalog",
] as const;
export type SearchResultType = (typeof SEARCH_RESULT_TYPES)[number];

export interface SearchResult {
  type: SearchResultType;
  id: string;
  number: string;
  label: string;
  sublabel?: string;
  status?: string;
  href: string;
}
